import { CalendarClock } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { display } from '@/lib/utils';
import { taskKindLabel, previewEntryDetail } from './order-labels';
import type { SchedulePreview } from './order-form';

function formatter(options: Intl.DateTimeFormatOptions, timeZone: string): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(display.locale, { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat(undefined, options);
  }
}

const STATE_MESSAGE: Record<Exclude<SchedulePreview['state'], 'scheduled'>, string> = {
  as_needed: 'io_form_preview_prn',
  no_tasks: 'io_form_preview_none',
  incomplete: 'io_form_preview_incomplete',
};

export function SchedulePreviewPanel({ preview, timeZone }: { preview: SchedulePreview; timeZone: string }) {
  const time = formatter({ hour: '2-digit', minute: '2-digit' }, timeZone);
  const day = formatter({ weekday: 'short', day: 'numeric', month: 'short' }, timeZone);
  return (
    <section aria-labelledby="order-preview-title" className="rounded-xl border bg-card shadow-sm lg:sticky lg:top-4">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <CalendarClock className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 leading-tight">
          <h2 id="order-preview-title" className="text-sm font-semibold">
            {tr('ipd', 'io_form_preview')}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{tr('ipd', 'io_form_preview_hint')}</p>
        </div>
      </header>
      <div className="px-4 py-3" aria-live="polite">
        {preview.state === 'scheduled' ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground">{tr('ipd', 'io_form_preview_count', { count: preview.total })}</p>
            <ol className="space-y-3 lg:max-h-[55vh] lg:overflow-y-auto lg:pe-1">
              {preview.days.map((group) => (
                <li key={group.date}>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{day.format(group.at)}</p>
                  <ul className="divide-y rounded-lg border">
                    {group.entries.map((entry) => {
                      const detail = previewEntryDetail(entry);
                      return (
                        <li key={`${entry.at.getTime()}-${entry.taskKind}-${entry.label}`} className="flex items-baseline gap-3 px-3 py-1.5 text-sm">
                          <span className="w-14 shrink-0 font-medium tabular-nums">{time.format(entry.at)}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{taskKindLabel(entry.taskKind)}</span>
                            {detail && <span className="block truncate text-xs text-muted-foreground">{detail}</span>}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ol>
            {preview.shown < preview.total && <p className="text-xs text-muted-foreground">{tr('ipd', 'io_form_preview_more', { shown: preview.shown, total: preview.total })}</p>}
            {preview.continues && <p className="text-xs text-muted-foreground">{tr('ipd', 'io_form_preview_beyond')}</p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{tr('ipd', STATE_MESSAGE[preview.state])}</p>
        )}
      </div>
    </section>
  );
}
